import { createEffect, onMount } from "solid-js"
import { BufferGeometry, Float32BufferAttribute, LineBasicMaterial, LineSegments, type Scene } from "three"
import { onCleanupRemoveAndDispose } from "../../../../rx/cleanup/onCleanupRemoveAndDispose"
import useGridWidthHeight from "../../../../rx/memo/useGridWidthHeight"
import { useTileEdgeListSignal } from "../../../../rx/signal/useTileEdgeListSignal"
import type { Engine } from "../../../core/engine/Engine.type"
import getTileGraphEdgeListSizeFromWidthHeight from "../../../core/engine/TileGraphEdge/getTileGraphEdgeListSizeFromWidthHeight"
import createOnFrameUpdateIsActiveSignal from "../../../event/GameObject3D/GameObject3DFrameUpdate/createOnFrameUpdateIsActiveSignal"
import { HALF_VERTICES_PER_TILE, VERTICES_PER_TILE } from "../../consts"

const positionsPerEdge = 2

export default function createTerrainMesh1V3GridEffect(engine: Engine, scene: Scene) {
  const getGridSize = useGridWidthHeight(engine)
  const [getTileEdgeList] = useTileEdgeListSignal(engine)

  createEffect(() => {
    const { width, height } = getGridSize()
    const edgesLen = getTileGraphEdgeListSizeFromWidthHeight(width, height)
    const positionsLen = edgesLen * positionsPerEdge * 3

    const geometry = new BufferGeometry()
    const positionsTarget = new Float32Array(positionsLen)
    const positionsAttribute = new Float32BufferAttribute(positionsTarget, 3)
    const positionsActual = positionsAttribute.array
    geometry.setAttribute("position", positionsAttribute)

    createEffect(() => {
      const edges = getTileEdgeList()
      for (let i = 0; i < edges.length; i++) {
        const edge = edges[i]
        const {
          elevation: elevationEdge,
          // rdi: getRidgeDirectionIndex,
        } = edge
        const {
          // diff: getElevationDiff,
          max: getElevationMax,
          // min: getElevationMin,
        } = elevationEdge

        const { x, y, horz } = edge
        const stride = i * positionsPerEdge * 3

        const x0 = (x + (horz ? 0 : 0.5)) * VERTICES_PER_TILE
        const y0 = VERTICES_PER_TILE * 0.2
        const z0 = (y + (horz ? 0.5 : 0)) * VERTICES_PER_TILE

        const x1 = (x + (horz ? 1 : 0.5)) * VERTICES_PER_TILE
        const y1 = VERTICES_PER_TILE * 0.2
        const z1 = (y + (horz ? 0.5 : 1)) * VERTICES_PER_TILE

        positionsTarget[stride] = x0
        positionsTarget[stride + 1] = y0
        positionsTarget[stride + 2] = z0

        positionsTarget[stride + 3] = x1
        positionsTarget[stride + 4] = y1
        positionsTarget[stride + 5] = z1

        createEffect(() => {
          const elevation = getElevationMax()
          const y = elevation * 0.5 * (VERTICES_PER_TILE / 10)
          positionsTarget[stride + 1] = y
          positionsTarget[stride + 4] = y
          setIsActive(true)
        })
      }

      setIsActive(true)
    })

    onMount(() => {
      positionsActual.set(positionsTarget)
      positionsAttribute.copyArray(positionsActual)
      positionsAttribute.needsUpdate = true
    })

    const material = new LineBasicMaterial({
      color: 0x999999,
    })
    const grid = new LineSegments(geometry, material)

    // Center it like you were doing with BatchedMesh
    grid.position.z -= HALF_VERTICES_PER_TILE
    grid.position.x -= HALF_VERTICES_PER_TILE

    grid.position.y += 0.1

    scene.add(grid)
    grid.visible = false

    let positionsAreProbablyDirty = true
    const [, setIsActive] = createOnFrameUpdateIsActiveSignal(engine, (delta: number) => {
      // console.log('frame')
      positionsAreProbablyDirty = false

      for (let i = 0; i < positionsActual.length; i += 3) {
        const indexY = i + 1
        const targetY = positionsTarget[indexY]
        const currentY = positionsActual[indexY]
        if (Math.abs(currentY - targetY) > 0.005) {
          positionsActual[indexY] = currentY + ((targetY - currentY) * 0.001 * delta)
          positionsAreProbablyDirty = true
        } else if (currentY !== targetY) {
          positionsActual[indexY] = targetY
          positionsAreProbablyDirty = true
        }
      }
      if (positionsAreProbablyDirty) {
        positionsAttribute.copyArray(positionsActual)
        positionsAttribute.needsUpdate = true
      } else {
        setIsActive(false)
      }
    })

    onMount(() => {
      for (let i = 0; i < positionsActual.length; i += 3) {
        const indexY = i + 1
        positionsActual[indexY] = 4
      }
      positionsAttribute.copyArray(positionsActual)
      positionsAttribute.needsUpdate = true

      setIsActive(true)
    })

    // SolidJS cleanup
    onCleanupRemoveAndDispose(grid)
  })
}
